import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { TopBar } from "@/components/layout/TopBar";
import { ShieldCheck, Info, AlertTriangle, CheckCircle2, Circle, Clock } from "lucide-react";

const OPTIM_LABELS = {
  fr: {
    career: "Carrière",
    future_income: "Revenus futurs",
    salary_progression: "Progression salariale",
    skills_to_acquire: "Compétences à acquérir",
    certifications: "Certifications",
    hidden_opportunities: "Opportunités cachées",
    international_mobility: "Mobilité internationale",
    financial_security: "Sécurité financière",
    social_benefits: "Avantages sociaux",
    gov_programs_access: "Programmes gouvernementaux",
    life_projects: "Projets de vie",
    quality_of_life: "Qualité de vie",
  },
  en: {
    career: "Career",
    future_income: "Future income",
    salary_progression: "Salary progression",
    skills_to_acquire: "Skills to acquire",
    certifications: "Certifications",
    hidden_opportunities: "Hidden opportunities",
    international_mobility: "International mobility",
    financial_security: "Financial security",
    social_benefits: "Social benefits",
    gov_programs_access: "Public programs",
    life_projects: "Life projects",
    quality_of_life: "Quality of life",
  },
};

export default function TrustCenter() {
  const { lang } = useI18n();
  const [spec, setSpec] = useState(null);
  const [disc, setDisc] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [f, d] = await Promise.all([
          api.get("/trust/framework"),
          api.get("/trust/disclaimers"),
        ]);
        setSpec(f.data);
        setDisc(d.data);
      } catch {}
    })();
  }, [lang]);

  const optim = OPTIM_LABELS[lang];

  return (
    <div>
      <TopBar
        title="Trusted Intelligence Framework™"
        subtitle={lang === "fr"
          ? "Explicabilité, transparence, aide à la décision — jamais garantie de résultat."
          : "Explainability, transparency, decision support — never a guarantee."}
      />
      <div className="px-6 md:px-10 py-8 space-y-8">

        {/* Posture card */}
        <div className="rounded-md border border-[#0B2545]/40 bg-gradient-to-br from-[#EBE8DE] to-[#FFFFFF] p-8 md:p-10 shadow-[0_0_60px_-24px_rgba(197,168,128,0.45)]">
          <div className="flex items-center gap-3 mb-6">
            <ShieldCheck className="h-5 w-5 text-[#0B2545]" strokeWidth={1.5} />
            <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#0B2545]">
              Posture · {spec?.posture || "decision_support_only"}
            </span>
          </div>
          <h2 className="font-display text-2xl md:text-3xl tracking-tight text-[#1F2937] max-w-3xl leading-tight">
            {lang === "fr"
              ? "Toute recommandation ALTIORA est une aide à la décision — personnalisée, explicable et transparente."
              : "Every ALTIORA recommendation is decision support — personalised, explainable and transparent."}
          </h2>
          <ol className="mt-8 space-y-3">
            {(spec?.principles || []).map((p, i) => (
              <li key={i} className="flex gap-4 text-sm text-[#5B5B62] leading-relaxed">
                <span className="font-mono text-[10px] text-[#0B2545] pt-1">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="text-[#E2E8F0]">{p}</span>
              </li>
            ))}
          </ol>
        </div>

        {/* Explanation contract */}
        <div className="rounded-md border border-[#E4E1D8] bg-white p-6">
          <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#5B5B62] mb-4">
            {lang === "fr" ? "Contrat d'explication (par recommandation)" : "Explanation contract (per recommendation)"}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              [lang === "fr" ? "Pourquoi cette recommandation" : "Why this recommendation", "why"],
              [lang === "fr" ? "Données du profil utilisées" : "Profile data used", "data_used[]"],
              [lang === "fr" ? "Hypothèses retenues" : "Assumptions retained", "assumptions[]"],
              [lang === "fr" ? "Niveau de confiance (0-1)" : "Confidence level (0-1)", "confidence"],
              [lang === "fr" ? "Actions pour améliorer" : "Actions to improve", "improve_by[]"],
              [lang === "fr" ? "Aide à la décision uniquement" : "Decision support only", "decision_support_only: true"],
            ].map(([label, key]) => (
              <div key={key} className="rounded-md border border-[#E4E1D8] bg-[#F8F7F3] px-4 py-3">
                <div className="text-sm text-[#1F2937]">{label}</div>
                <div className="mt-1 font-mono text-[10px] text-[#0B2545]">{key}</div>
              </div>
            ))}
          </div>
        </div>

        {/* 12 optimisation targets */}
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#0B2545] mb-4">
            {lang === "fr" ? "12 cibles d'optimisation" : "12 optimisation targets"}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {(spec?.optimisation_targets || []).map((k, idx) => (
              <div key={k} className="rounded-md border border-[#E4E1D8] bg-white p-4">
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#5B5B62]">
                  {String(idx + 1).padStart(2, "0")}
                </div>
                <div className="mt-1 text-sm text-[#1F2937]">{optim[k] || k}</div>
                <div className="mt-1 font-mono text-[9px] text-[#0B2545]">{k}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Life projects scope */}
        <div className="rounded-md border border-[#E4E1D8] bg-white p-6">
          <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#5B5B62] mb-4">
            {lang === "fr" ? "Projets de vie couverts" : "Life projects covered"}
          </div>
          <div className="flex flex-wrap gap-2">
            {(spec?.life_projects_scope || []).map((s) => (
              <span key={s} className="rounded-md border border-[#E4E1D8] bg-[#F8F7F3] px-3 py-1.5 text-xs text-[#1F2937]">
                {s}
              </span>
            ))}
          </div>
        </div>

        {/* Engine adoption status */}
        <div className="rounded-md border border-[#E4E1D8] bg-white overflow-hidden">
          <div className="px-5 py-3 text-[10px] font-mono uppercase tracking-[0.2em] text-[#5B5B62] border-b border-[#E4E1D8]">
            {lang === "fr" ? "Adoption par moteur" : "Per-engine adoption"}
          </div>
          {Object.entries(spec?.adoption_status_per_engine || {}).map(([engine, status]) => (
            <div key={engine} className="grid grid-cols-[1fr_140px] px-5 py-3 border-b border-[#E4E1D8] last:border-b-0 items-center">
              <div className="font-mono text-xs text-[#1F2937]">{engine}</div>
              <div className="flex items-center gap-2 justify-end font-mono text-[10px] uppercase tracking-widest">
                {status === "adopted" ? (
                  <><CheckCircle2 className="h-3.5 w-3.5 text-[#2E8B57]" strokeWidth={1.5} /><span className="text-[#2E8B57]">adopté</span></>
                ) : status === "partial" ? (
                  <><Clock className="h-3.5 w-3.5 text-[#0B2545]" strokeWidth={1.5} /><span className="text-[#0B2545]">partiel</span></>
                ) : (
                  <><Circle className="h-3.5 w-3.5 text-[#5B5B62]" strokeWidth={1.5} /><span className="text-[#5B5B62]">prévu</span></>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Disclaimers catalogue */}
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#D4AF37] mb-4 flex items-center gap-2">
            <AlertTriangle className="h-3.5 w-3.5" strokeWidth={1.5} />
            {lang === "fr" ? "Avertissements standardisés" : "Standardised disclaimers"}
          </div>
          <div className="space-y-3">
            {Object.entries(disc?.items || {}).map(([key, text]) => (
              <div key={key} className="rounded-md border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-4">
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37] mb-1">{key}</div>
                <p className="text-xs text-[#5B5B62] leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Architectural promise + vision */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="rounded-md border border-[#E4E1D8] bg-white p-6">
            <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#0B2545] mb-3">
              {lang === "fr" ? "Promesse architecturale" : "Architectural promise"}
            </div>
            <p className="text-sm text-[#E2E8F0] leading-relaxed">
              {spec?.architectural_promise}
            </p>
          </div>
          <div className="rounded-md border border-[#0B2545]/40 bg-gradient-to-br from-[#EBE8DE] to-[#FFFFFF] p-6">
            <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#0B2545] mb-3">
              {lang === "fr" ? "Vision" : "Vision"}
            </div>
            <p className="text-sm text-[#1F2937] leading-relaxed">
              {spec?.vision}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
